'use client';

import { Box, Theme } from '@mui/material';
import { keyframes } from '@mui/system';
import { withClarityTheme } from '@verifime/styles';
import { ReactElement } from 'react';

export type BrandedDotLoaderProps = {
  'data-testid'?: string;
};

// Three dots bounce in sequence, flicking from the brand primary to the secondary colour at the top
// of each hop. Colours resolve from the theme (--dot-base / --dot-peak, set per dot); the small
// pixel geometry (10px diameter, 5px hop, the 1.5s cycle and its 0.15s stagger) is the loader's own
// intrinsic shape rather than a token, and it matches the design board's own `.dot` rule exactly.
const dotBounce = keyframes`
  0%, 15%, 100% { transform: translateY(0) scale(1); background-color: var(--dot-base); }
  7% { transform: translateY(-5px); background-color: var(--dot-peak); }
`;

/**
 * The brand's three-dot loader, on its own. Extracted from AuthGateLoadingScreen (since renamed
 * LoadingScreen), which still renders it, so that the magic-link "Signing you in ..." screen
 * (design board 1b) can show the same loader inside a content column with its own logo, heading and
 * body copy rather than inside LoadingScreen's full-page 100vh frame with its mark-only logo.
 *
 * This component is the dots and nothing else. It renders no logo, because its two callers want
 * different logo layouts, and it carries no status role, because the caller owns what is being
 * announced. It is aria-hidden for the same reason: a bouncing dot has nothing to say to a screen
 * reader, and the caller's own status region is what does the announcing.
 *
 * Wrapped in withClarityTheme for the same reason ExpiryClock and LinkSent are: the dot base colour
 * (theme.palette.permanent.primary) only exists on the Clarity theme, and this component has no
 * control over the theme a future caller renders it under. Nesting withClarityTheme inside an
 * already-Clarity ancestor is idempotent, so it costs nothing where it is already wrapped.
 */
function BrandedDotLoaderComponent({
  'data-testid': dataTestId = 'branded-dot-loader',
}: Readonly<BrandedDotLoaderProps>): ReactElement {
  return (
    <Box
      data-testid={dataTestId}
      aria-hidden
      sx={(theme: Theme) => ({
        display: 'flex',
        gap: theme.semantic.spacing.xs,
        alignItems: 'center',
      })}
    >
      {[0, 1, 2].map((index) => (
        <Box
          key={index}
          sx={(theme: Theme) => ({
            width: 10,
            height: 10,
            borderRadius: '50%',
            // permanent.primary, NOT palette.primary.main: permanent holds Eggplant 500 in both
            // themes, where palette.primary.main drops to Eggplant 300 in dark. The design board's
            // own dot rule reads var(--color-permanent-primary), and a brand mark should read as
            // the brand in either theme. See clarityLightTheme.ts's own comment on palette.permanent
            // for the precedent this follows.
            '--dot-base': theme.palette.permanent.primary,
            // Already Kale 500 in both themes, so it needs no permanent equivalent (and the board
            // has none: its tokens.css defines no --color-permanent-secondary at all).
            '--dot-peak': theme.palette.secondary.main,
            backgroundColor: 'var(--dot-base)',
            animation: `${dotBounce} 1.5s ease-in-out ${index * 0.15}s infinite both`,
            // The design board's own .dot rule carries this and the shipped loader never did. A
            // decorative bounce is exactly what a viewer who asks for reduced motion is asking to
            // be rid of.
            '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
          })}
        />
      ))}
    </Box>
  );
}

export const BrandedDotLoader = withClarityTheme(BrandedDotLoaderComponent);
